import { type Citation, type CitationSource, type FieldCitations } from '../../../models/Citation'
import { isBlankValue, isRecord, isUnanswered, type Values } from './schemaValues'

// RJSF joins a field's path with `_` under a `root` prefix; schema keys are alphanumeric, so the split is unambiguous.
export const citationPath = (fieldId: string): string =>
  fieldId
    .replace(/^root_?/, '')
    .split('_')
    .filter(Boolean)
    .join('.')

export const citationsFor = (fieldId: string, citations: FieldCitations | undefined): Citation[] => {
  const path = citationPath(fieldId)
  return path === '' ? [] : (citations?.[path] ?? [])
}

export const valueAt = (values: Values, path: string): unknown =>
  path
    .split('.')
    .filter(Boolean)
    .reduce<unknown>((node, key) => (node && typeof node === 'object' ? (node as Record<string, unknown>)[key] : undefined), values)

const sameValue = (a: unknown, b: unknown): boolean => {
  if (isBlankValue(a) && isBlankValue(b)) {
    return true
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((entry, index) => sameValue(entry, b[index]))
  }
  if (isRecord(a) && isRecord(b)) {
    return [...new Set([...Object.keys(a), ...Object.keys(b)])].every((key) => sameValue(a[key], b[key]))
  }
  return Object.is(a, b)
}

const pick = (citations: FieldCitations | undefined, keep: (path: string) => boolean): FieldCitations =>
  Object.fromEntries(Object.entries(citations ?? {}).filter(([path, entries]) => entries.length > 0 && keep(path)))

// A citation describes the value the agent filled; once that value changes or is cleared it no longer applies.
export const pruneCitations = (previous: Values, next: Values, citations: FieldCitations | undefined): FieldCitations =>
  pick(citations, (path) => {
    const value = valueAt(next, path)
    return !isBlankValue(value) && sameValue(valueAt(previous, path), value)
  })

// Only the keys `mergePrefilled` actually took from the agent carry its citations: a key the user had already answered keeps no citation,
// even when the agent answered the same value.
export const keepMergedCitations = (existing: Values, filled: Values, citations: FieldCitations | undefined): FieldCitations =>
  pick(citations, (path) => {
    const top = path.split('.')[0]
    return top in filled && isUnanswered(existing[top]) && !isBlankValue(valueAt(filled, path))
  })

export const withCitations = (existing: FieldCitations | undefined, added: FieldCitations): FieldCitations => ({ ...existing, ...added })

const isOptionalString = (value: unknown): boolean => value === undefined || typeof value === 'string'

// The source labels read these ids as strings, so a source of the wrong shape would break the form while rendering.
const isCitationSource = (source: unknown): source is CitationSource => {
  if (!isRecord(source)) {
    return false
  }
  if (source.type === 'meeting') {
    return typeof source.room_id === 'string' && typeof source.session_id === 'string'
  }
  if (source.type === 'external') {
    return typeof source.data_source_id === 'string' && isOptionalString(source.name) && isOptionalString(source.client_id)
  }
  return typeof source.type === 'string' && typeof source.service_id === 'string'
}

const isCitation = (value: unknown): value is Citation =>
  isRecord(value) && typeof value.id === 'string' && typeof value.description === 'string' && isCitationSource(value.source)

// Reads the agent's `citations` object, dropping anything that is not a list of well-formed citations.
export const asFieldCitations = (value: unknown): FieldCitations => {
  if (!isRecord(value)) {
    return {}
  }
  return Object.fromEntries(
    Object.entries(value)
      .map(([path, entries]) => [path, Array.isArray(entries) ? entries.filter(isCitation) : []] as const)
      .filter(([, entries]) => entries.length > 0),
  )
}
